/**
 * Shop Management API service
 * Handles shop CRUD operations, search, and filtering
 */

import { apiClient } from './client';
import { Shop, ShopCreateRequest, ShopUpdate, ShopType } from '../types';
import { getConfig, createLogger } from '../config/environment';

const logger = createLogger('ShopManagementService');
const config = getConfig();

export class ShopManagementService {
  /**
   * Create a new shop
   * POST /api/shops
   * Automatically creates ADMIN user if password provided
   */
  async createShop(request: ShopCreateRequest): Promise<Shop> {
    try {
      logger.info('Creating new shop');
      const response = await apiClient.post<Shop>(config.shopsEndpoint, request);

      if (response.status !== 201 && response.status !== 200) {
        throw new Error('Failed to create shop');
      }

      logger.info('Shop created successfully', { shopId: response.data.id });
      return response.data;
    } catch (error) {
      logger.error('Failed to create shop', error);
      throw error;
    }
  }

  /**
   * Get all shops
   * GET /api/shops
   */
  async getAllShops(): Promise<Shop[]> {
    try {
      logger.info('Fetching all shops');
      const response = await apiClient.get<Shop[]>(config.shopsEndpoint);

      if (response.status !== 200) {
        throw new Error('Failed to fetch shops');
      }

      logger.info(`Fetched ${response.data.length} shops`);
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch all shops', error);
      throw error;
    }
  }

  /**
   * Get shop by ID
   * GET /api/shops/{shopId}
   *
   * Response structure:
   * {
   *   shop: { ... shop data ... },
   *   subscriptions: [ ... subscription data ... ]
   * }
   */
  async getShop(shopId: string): Promise<Shop> {
    try {
      logger.info(`Fetching shop: ${shopId}`);
      const response = await apiClient.get<any>(`${config.shopsEndpoint}/${shopId}`);

      if (response.status !== 200) {
        throw new Error('Failed to fetch shop');
      }

      // Handle new response structure with shop and subscriptions
      // If response has 'shop' property, extract it and merge subscriptions
      let shopData: Shop;

      if (response.data.shop) {
        // New structure: { shop: {...}, subscriptions: [...] }
        shopData = {
          ...response.data.shop,
          subscriptions: response.data.subscriptions || []
        };
      } else {
        // Old structure: direct shop data (backward compatibility)
        shopData = response.data;
      }

      logger.info('Shop data fetched successfully', {
        shopId: shopData.id,
        hasSubscriptions: !!(shopData.subscriptions && shopData.subscriptions.length > 0),
        subscriptionCount: shopData.subscriptions?.length || 0
      });

      return shopData;
    } catch (error) {
      logger.error('Failed to fetch shop', error);
      throw error;
    }
  }

  /**
   * Update shop details
   * PUT /api/shops/{shopId}
   *
   * After updating, fetches the full shop data including subscriptions
   * to ensure store is synchronized with backend state
   */
  async updateShop(shopId: string, request: ShopUpdate): Promise<Shop> {
    try {
      logger.info(`Updating shop: ${shopId}`);
      const response = await apiClient.put<Shop>(
        `${config.shopsEndpoint}/${shopId}`,
        request
      );

      if (response.status !== 200) {
        throw new Error('Failed to update shop');
      }

      logger.info('Shop updated successfully, fetching complete shop data with subscriptions');

      // Fetch the complete shop data (including subscriptions) to ensure store is synchronized
      // This ensures subscription data is included in the returned shop object
      const completeShopData = await this.getShop(shopId);

      logger.info('✅ Shop update complete with subscriptions', {
        shopId: completeShopData.id,
        subscriptionCount: completeShopData.subscriptions?.length || 0,
        hasActiveSubscriptions: (completeShopData.subscriptions || []).some(s => s.isActive)
      });

      return completeShopData;
    } catch (error) {
      logger.error('Failed to update shop', error);
      throw error;
    }
  }

  /**
   * Delete shop
   * DELETE /api/shops/{shopId}
   * SUPER_ADMIN only
   */
  async deleteShop(shopId: string): Promise<void> {
    try {
      logger.info(`Deleting shop: ${shopId}`);
      const response = await apiClient.delete(`${config.shopsEndpoint}/${shopId}`);

      if (response.status !== 204 && response.status !== 200) {
        throw new Error('Failed to delete shop');
      }

      logger.info('Shop deleted successfully');
    } catch (error) {
      logger.error('Failed to delete shop', error);
      throw error;
    }
  }

  /**
   * Update shop status (online/offline)
   * PATCH /api/shops/{shopId}
   * SUPER_ADMIN only
   *
   * @param shopId - Shop ID
   * @param isLive - true for online, false for offline
   */
  async updateShopStatus(shopId: string, isLive: boolean): Promise<Shop> {
    try {
      logger.info(`Updating shop status: ${shopId}`, { isLive });
      const response = await apiClient.patch<Shop>(
        `${config.shopsEndpoint}/${shopId}`,
        { isLive }
      );

      if (response.status !== 200) {
        throw new Error('Failed to update shop status');
      }

      logger.info('Shop status updated successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to update shop status', error);
      throw error;
    }
  }

  /**
   * Delete all shops (use with caution)
   * DELETE /api/shops
   */
  async deleteAllShops(): Promise<void> {
    try {
      logger.warn('Deleting all shops');
      const response = await apiClient.delete(config.shopsEndpoint);

      if (response.status !== 204 && response.status !== 200) {
        throw new Error('Failed to delete all shops');
      }

      logger.info('All shops deleted successfully');
    } catch (error) {
      logger.error('Failed to delete all shops', error);
      throw error;
    }
  }

  /**
   * Search shops by name
   * GET /api/shops/search/name?name={name}
   */
  async searchShopsByName(name: string): Promise<Shop[]> {
    try {
      logger.info(`Searching shops by name: ${name}`);
      const response = await apiClient.get<Shop[]>(
        `${config.shopsEndpoint}/search/name`,
        { params: { name } }
      );

      if (response.status !== 200) {
        throw new Error('Failed to search shops by name');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to search shops by name', error);
      throw error;
    }
  }

  /**
   * Search shops by owner
   * GET /api/shops/search/owner?owner={owner}
   */
  async searchShopsByOwner(owner: string): Promise<Shop[]> {
    try {
      logger.info(`Searching shops by owner: ${owner}`);
      const response = await apiClient.get<Shop[]>(
        `${config.shopsEndpoint}/search/owner`,
        { params: { owner } }
      );

      if (response.status !== 200) {
        throw new Error('Failed to search shops by owner');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to search shops by owner', error);
      throw error;
    }
  }

  /**
   * Search shops by address
   * GET /api/shops/search/address?address={address}
   */
  async searchShopsByAddress(address: string): Promise<Shop[]> {
    try {
      logger.info(`Searching shops by address: ${address}`);
      const response = await apiClient.get<Shop[]>(
        `${config.shopsEndpoint}/search/address`,
        { params: { address } }
      );

      if (response.status !== 200) {
        throw new Error('Failed to search shops by address');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to search shops by address', error);
      throw error;
    }
  }

  /**
   * Filter shops by type
   * GET /api/shops/search/type?shopType={shopType}
   */
  async filterShopsByType(shopType: ShopType): Promise<Shop[]> {
    try {
      logger.info(`Filtering shops by type: ${shopType}`);
      const response = await apiClient.get<Shop[]>(
        `${config.shopsEndpoint}/search/type`,
        { params: { shopType } }
      );

      if (response.status !== 200) {
        throw new Error('Failed to filter shops by type');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to filter shops by type', error);
      throw error;
    }
  }
}

// Create singleton instance
export const shopManagementService = new ShopManagementService();

